<template>
  <div>
    <!-- General messages -->
    <div class="mt-2">
      <div class="row mb-3" v-if="!this.$root.me.grants.combined.length">
        <div class="col-xl card card-body text-left">
          <div v-if="$root.hasRole('admin.*')">
            <p class="font-weight-bold">Welcome administrator!</p>
            <p>Looks there are no websites setup, create one to get started.</p>
            <router-link :to="{ name: 'websites-add' }" class="btn btn-create">Add your first website</router-link>
          </div>
          <div v-else>
            Welcome, looks there are no websites setup. Contact your account administrator to setup the first one.
          </div>
        </div>
      </div>

      <div class="row mb-3" v-if="showSetupPrompt && !canEdit">
        <div class="col-xl card card-body text-left">
          <h4 class="text-primary"><span class="icon-rocket-blue mr-1">&nbsp;</span> Almost there!</h4>
          <p>Please contact your account administrator, who has permissions to complete your account setup.</p>
          <span class="m-0">For the time being, have a look at our <a href="https://support.exatom.io/" target="_blank">knowledge base</a> to discover the world of Form Analytics and Smart Tooltips.</span>
        </div>
      </div>
      <div class="row mb-3" v-if="showSetupPrompt && canEdit && (!metrics || !metrics.period.length) && !loading">
        <div class="col-xl card card-body text-left">
          <h4 class="text-primary"><span class="icon-rocket-blue mr-1">&nbsp;</span> Almost there!</h4>
          <span>Please <router-link :to="{ name: 'onboarding' }" class="text-primary">complete</router-link> your account setup.</span>
        </div>
      </div>

      <div class="row mb-3" v-if="$route.query.src === 'dashboardSetupComplete'">
        <div class="col-xl card card-body text-left">
          <div class="font-weight-bold text-primary cursor-pointer" style="position: absolute; right: 24px; top: 16px;" @click="$event.target.parentElement.style.display = 'none'">&cross;</div>
          <h4 class="text-primary"><i class="material-icons" style="vertical-align: text-bottom;">done</i> Congratulations, your account is set up!</h4>
          <span>Visit our <a href="https://support.exatom.io/" target="_blank">knowledge base</a> for a quick introduction.</span>
        </div>
      </div>

      <div class="row mb-3" v-if="metrics && metrics.period.length && !metrics.period[0].sessions">
        <div class="col-xl card card-body bg-primary text-white">
          We know you're working on it! Once your website is tagged and you've added your first form, insights will be available in a matter of minutes.
        </div>
      </div>
    </div>

    <b-alert :show="includeToday" class="card card-body mb-3 bg-exatom-green">
      By default we only show complete data. As your account was created today, we've included today's data.
    </b-alert>

    <!-- Highlights -->
    <div class="d-flex justify-content-between align-items-end" v-if="metrics && metrics.period.length">
      <h4 class="card-title line-after mb-4">All forms dashboard</h4>
      <!-- date selector -->
      <div id="dashboard-date" class="text-right mb-3">
        <b-form-radio-group
          name="dateSelector"
          class="selector"
          v-model="selectedDate"
          :options="availableDates"
          buttons
        ></b-form-radio-group>
        <span class="m-0 mt-2 badge badge-success d-block" style="font-size: 0.75rem; font-weight: bold; padding-top: 0.6em;">
            {{ selectedDateFrom | date }} <span class="material-icons md-14 arrow-east">east</span> {{ selectedDateTo | date }}
        </span>
      </div>
    </div>
    <div class="row" v-if="metrics && metrics.period.length">
      <div class="col-lg-2 mb-3">
        <div ref="missedConversionOpportunity" class="missedConversionOpportunitySample"></div>
        <div class="card card-body h-100 cursor-pointer d-flex justify-content-between"
             @click="selectedConversionPath = (selectedConversionPath !== 'abandoners' ? 'abandoners' : null)">
          <div class="lines-top"><h5>Missed<br />conversion<br />opportunity</h5></div>
          <div class="display-5 font-weight-bold mb-2" :class="metrics.period[0].total_abandoned_value ? 'text-danger' : 'text-primary'">
            {{ $root.clientCurrencyFormat(metrics.period[0].total_abandoned_value) }}
          </div>
          <div class="text-danger" style="font-size: 12px;">{{ metrics.period[0].total_abandonment_rate | numeral('0,0') }} %</div>
        </div>
      </div>
      <div class="col-lg-2 mb-3">
        <div class="card card-body h-100 cursor-pointer d-flex justify-content-between"
             @click="selectedConversionPath = (selectedConversionPath !== 'conversions' ? 'conversions' : null)">
          <div class="lines-top"><h5>Total<br />conversion<br />value</h5></div>
          <div class="display-5 font-weight-bold text-primary mb-2">{{ $root.clientCurrencyFormat(metrics.period[0].total_conversion_value) }}</div>
          <div class="invisible" style="font-size: 12px;">n/a</div>
        </div>
      </div>
      <div class="col-lg-2 mb-3">
        <div class="card card-body h-100 d-flex justify-content-between">
          <div class="lines-top"><h5>Average<br />conversion<br />value</h5></div>
          <div class="display-5 font-weight-bold text-primary mb-2">{{ $root.clientCurrencyFormat(metrics.period[0].average_conversion_value) }}</div>
          <div class="invisible" style="font-size: 12px;">n/a</div>
        </div>
      </div>
      <div class="col-lg-2 mb-3" :set="metric = { value: metrics.period[0].hesitation_time || 0, trend: metrics.period[0].hesitation_time_trend_rate || 0 }">
        <div class="card card-body h-100 cursor-pointer d-flex justify-content-between"
             @click="selectedConversionPath = (selectedConversionPath !== 'hesitation_time' ? 'hesitation_time' : null)">
          <div class="lines-top"><h5>Hesitation<br />time</h5></div>
          <div class="display-5 font-weight-bold text-primary mb-2">{{ metric.value | timeSeconds }}</div>
          <div v-if="metric.trend" :class="metric.trend ? (metric.trend <= 0 ? 'text-success' : 'text-danger') : 'text-secondary'" style="font-size: 12px;">
            <metric-icon v-if="metric.trend" :upwards="metric.trend > 0" :color="metric.trend < 0 ? 'green' : 'red'" />{{ metric.trend | numeral('0,0.0') }}%
          </div>
          <div v-if="!metric.trend" class="invisible" style="font-size: 12px;">n/a</div>
        </div>
      </div>
      <div class="col-lg-2 mb-3" :set="metric = { value: metrics.period[0].duration_to_conversion || 0, trend: metrics.period[0].duration_to_conversion_trend_rate || 0 }">
        <div class="card card-body h-100 cursor-pointer d-flex justify-content-between"
             @click="selectedConversionPath = (selectedConversionPath !== 'duration_to_conversion' ? 'duration_to_conversion' : null)">
          <div class="lines-top"><h5>Time<br />to conversion</h5></div>
          <div class="display-5 font-weight-bold text-primary mb-2">{{ metric.value | timeSeconds }}</div>
          <div v-if="metric.trend" :class="metric.trend ? (metric.trend <= 0 ? 'text-success' : 'text-danger') : 'text-secondary'" style="font-size: 12px;">
            <metric-icon v-if="metric.trend" :upwards="metric.trend > 0" :color="metric.trend < 0 ? 'green' : 'red'" />{{ metric.trend | numeral('0,0.0') }}%
          </div>
          <div v-if="!metric.trend" class="invisible" style="font-size: 12px;">n/a</div>
        </div>
      </div>
      <div class="col-lg-2 mb-3" :set="metric = { value: metrics.period[0].error_rate || 0, trend: metrics.period[0].error_trend_rate || 0 }">
        <div class="card card-body h-100 cursor-pointer d-flex justify-content-between"
             @click="selectedConversionPath = (selectedConversionPath !== 'errors' ? 'errors' : null)">
          <div class="lines-top"><h5>Error rate</h5></div>
          <div class="display-5 font-weight-bold text-primary mb-2">{{ metric.value | numeral('0,0.0') }}%</div>
          <div v-if="metric.trend" :class="metric.trend ? (metric.trend <= 0 ? 'text-success' : 'text-danger') : 'text-secondary'" style="font-size: 12px;">
            <metric-icon :upwards="metric.trend > 0" :color="metric.trend < 0 ? 'green' : 'red'" />{{ metric.trend | numeral('0,0.0') }}%
          </div>
          <div v-if="!metric.trend" class="invisible" style="font-size: 12px;">n/a</div>
        </div>
      </div>
    </div>

    <!-- Conversion path metrics -->
    <div v-if="metrics && metrics.period && metrics.period.length" class="row card card-body mb-3 mx-0 pb-0 text-left" style="overflow: scroll;">
      <!-- graph -->
      <i class="material-icons display-4 text-primary">merge_type</i>
      <h5 class="mb-1">Conversion path</h5>
      <div>Steps your visitors took to submit the form</div>
      <div class="position-relative mx-auto" :style="{ width: '1086px', height: '465px', 'background-image': `url(${require('@/assets/conversion-funnel-v8.svg')})`}" style="background-size: 900px 370px; background-repeat: no-repeat; background-position-x: 94px; background-position-y: 23px;">
        <div ref="conversionPathStarts" class="conversionPathMetricSample"></div>
        <div @click="selectedConversionPath = (selectedConversionPath !== 'sessions' ? 'sessions' : null)"
             :set="metric = { name: 'Sessions', metric: metrics.period[0].sessions, trend_rate: metrics.period[0].sessions_trend_rate }">
          <div class="text-center conversionPathMetric" style="left: -44px; top: 209px;"
               :set="metric = { ...metric, badge_color: metric.trend_rate !== 0 ? (metric.trend_rate >= 0 ? 'badge-success' : 'badge-danger') : 'badge-secondary' }">
            <span class="material-symbols-outlined text-primary" style="font-size: 32px;">home</span>
            <div class="text-big font-weight-bold">{{ metric.name }}</div>
            <div class="text-big font-weight-bold text-primary m-1" style="font-size: 1.25rem !important;">{{ metric.metric | numeral }}</div>
            <div class="small-90" :class="metric.trend_rate !== 0 ? (metric.trend_rate >= 0 ? 'text-success' : 'text-danger') : 'text-secondary'">
              <metric-icon :upwards="metric.trend_rate !== 0 ? metric.trend_rate >= 0 : null" :color="metric.trend_rate >= 0 ? 'green' : 'red'" />
              {{ metric.trend_rate | numeral('0,0.0') }}%
            </div>
          </div>
        </div>
        <div @click="selectedConversionPath = (selectedConversionPath !== 'pre_start_abandoners' ? 'pre_start_abandoners' : null)"
             :set="metric = { name: 'Page abandoners', metric: metrics.period[0].pre_start_abandoners, rate: metrics.period[0].pre_start_abandonment_rate, trend_rate: metrics.period[0].pre_start_abandonment_trend_rate }">
          <div class="text-center conversionPathMetric" style="left: 156px; top: 314px;"
               :set="metric = { ...metric, badge_color: metric.trend_rate !== 0 ? (metric.trend_rate >= 0 ? 'badge-success' : 'badge-danger') : 'badge-secondary' }">
            <span class="material-icons text-danger mb-2">highlight_off</span>
            <div class="text-big font-weight-bold">{{ metric.name }}</div>
            <div class="text-big font-weight-bold text-primary m-1" style="font-size: 1.25rem !important;">{{ metric.metric | numeral }}</div>
            <div class="small-90" :class="metric.trend_rate !== 0 ? (metric.trend_rate >= 0 ? 'text-success' : 'text-danger') : 'text-secondary'">
              <metric-icon :upwards="metric.trend_rate !== 0 ? metric.trend_rate >= 0 : null" :color="metric.trend_rate >= 0 ? 'green' : 'red'" />
              {{ metric.trend_rate | numeral('0,0.0') }}%
            </div>
            <div class="font-weight-bold position-absolute text-right exatom-grey-plus1 metricRate" style="top: 38px; left: -64px;">
              {{ metric.rate | numeral('0,0.0') }}%
            </div>
          </div>
        </div>
        <div @click="selectedConversionPath = (selectedConversionPath !== 'starts' ? 'starts' : null)"
             :set="metric = { name: 'Starts', metric: metrics.period[0].starts, rate: (metrics.period[0].starts / metrics.period[0].sessions * 100), trend_rate: metrics.period[0].start_trend_rate }">
          <div class="text-center conversionPathMetric" style="left: 156px; top: 176px;"
               :set="metric = { ...metric, badge_color: metric.trend_rate !== 0 ? (metric.trend_rate >= 0 ? 'badge-success' : 'badge-danger') : 'badge-secondary' }">
            <span class="icon-rocket-blue-outline mt-2 mb-2">&nbsp;</span>
            <div class="text-big font-weight-bold">{{ metric.name }}</div>
            <div class="text-big font-weight-bold text-primary m-1" style="font-size: 1.25rem !important;">{{ metric.metric | numeral }}</div>
            <div class="small-90" :class="metric.trend_rate !== 0 ? (metric.trend_rate >= 0 ? 'text-success' : 'text-danger') : 'text-secondary'" v-b-tooltip title="Trend metric">
              <metric-icon :upwards="metric.trend_rate !== 0 ? metric.trend_rate >= 0 : null" :color="metric.trend_rate >= 0 ? 'green' : 'red'" />
              {{ metric.trend_rate | numeral('0,0.0') }}%
            </div>
            <div class="font-weight-bold position-absolute text-right exatom-grey-plus1 metricRate" style="top: 69px; left: -64px;">
              {{ metric.rate | numeral('0,0.0') }}%
            </div>
          </div>
        </div>
        <div @click="selectedConversionPath = (selectedConversionPath !== 'submits' ? 'submits' : null)"
             :set="metric = { name: 'Submits', metric: metrics.period[0].submits, rate: (metrics.period[0].submits / metrics.period[0].starts * 100), trend_rate: metrics.period[0].submit_trend_rate }">
          <div class="text-center conversionPathMetric" style="left: 356px; top: 77px;"
               :set="metric = { ...metric, badge_color: metric.trend_rate !== 0 ? (metric.trend_rate >= 0 ? 'badge-success' : 'badge-danger') : 'badge-secondary' }">
            <span class="icon-flag-blue mt-2 mb-2">&nbsp;</span>
            <div class="text-big font-weight-bold">{{ metric.name }}</div>
            <div class="text-big font-weight-bold text-primary m-1" style="font-size: 1.25rem !important;">{{ metric.metric | numeral }}</div>
            <div class="small-90" :class="metric.trend_rate !== 0 ? (metric.trend_rate >= 0 ? 'text-success' : 'text-danger') : 'text-secondary'" v-b-tooltip title="Trend metric">
              <metric-icon v-if="metric.trend_rate !== 0" :upwards="metric.trend_rate >= 0" :color="metric.trend_rate >= 0 ? 'green' : 'red'" />
              {{ metric.trend_rate | numeral('0,0.0') }}%
            </div>
            <div class="font-weight-bold position-absolute text-right exatom-grey-plus1 metricRate" style="top: 114px; right: 0; transform: translateX(-202px);">
              {{ metric.rate | numeral('0,0.0') }}%
            </div>
          </div>
        </div>
        <div @click="selectedConversionPath = (selectedConversionPath !== 'initial_abandoners' ? 'initial_abandoners' : null)"
             :set="metric = { name: 'Initial abandoners', metric: metrics.period[0].initial_abandoners, rate: metrics.period[0].initial_abandonment_rate, trend_rate: metrics.period[0].initial_abandonment_trend_rate }">
          <div class="text-center conversionPathMetric" style="left: 356px; top: 282px;"
               :set="metric = { ...metric, badge_color: metric.trend_rate !== 0 ? (metric.trend_rate <= 0 ? 'badge-success' : 'badge-danger') : 'badge-secondary' }">
            <span class="material-icons text-danger mb-2">highlight_off</span>
            <div class="text-big font-weight-bold">{{ metric.name }}</div>
            <div class="text-big font-weight-bold text-primary m-1" style="font-size: 1.25rem !important;">{{ metric.metric | numeral }}</div>
            <div class="small-90" :class="metric.trend_rate !== 0 ? (metric.trend_rate <= 0 ? 'text-success' : 'text-danger') : 'text-secondary'" v-b-tooltip title="Trend metric">
              <metric-icon :upwards="metric.trend_rate !== 0 ? metric.trend_rate >= 0 : null" :color="metric.trend_rate <= 0 ? 'green' : 'red'" />
              {{ metric.trend_rate | numeral('0,0.0') }}%
            </div>
            <div class="font-weight-bold position-absolute text-right exatom-grey-plus1 metricRate" style="top: 23px; right: 0; transform: translateX(-202px);">
              {{ metric.rate | numeral('0,0.0') }}%
            </div>
          </div>
        </div>
        <div @click="selectedConversionPath = (selectedConversionPath !== 'submits_once_success' ? 'submits_once_success' : null)"
             :set="metric = { name: 'Submit and convert', metric: metrics.period[0].submits_once_success, rate: (metrics.period[0].submits_once_success / metrics.period[0].submits * 100), trend_rate: metrics.period[0].submit_once_success_trend_rate }">
          <div class="text-center conversionPathMetric" style="left: 556px; top: -22px;"
               :set="metric = { ...metric, badge_color: metric.trend_rate !== 0 ? (metric.trend_rate >= 0 ? 'badge-success' : 'badge-danger') : 'badge-secondary' }">
            <span class="icon-flag-blue mt-2 mb-2">&nbsp;</span>
            <div class="text-big font-weight-bold">{{ metric.name }}</div>
            <div class="text-big font-weight-bold text-primary m-1" style="font-size: 1.25rem !important;">{{ metric.metric | numeral }}</div>
            <div class="small-90" :class="metric.trend_rate !== 0 ? (metric.trend_rate >= 0 ? 'text-success' : 'text-danger') : 'text-secondary'" v-b-tooltip title="Trend metric">
              <metric-icon v-if="metric.trend_rate !== 0" :upwards="metric.trend_rate >= 0" :color="metric.trend_rate >= 0 ? 'green' : 'red'" />
              {{ metric.trend_rate | numeral('0,0.0') }}%
            </div>
            <div class="font-weight-bold position-absolute text-right exatom-grey-plus1 metricRate" style="top: 107px; right: 0; transform: translateX(-202px);">
              {{ metric.rate | numeral('0,0.0') }}%
            </div>
          </div>
        </div>
        <div @click="selectedConversionPath = (selectedConversionPath !== 'submits_failed' ? 'submits_failed' : null)"
             :set="metric = { name: 'Submit failed', metric: metrics.period[0].submits_failed, rate: (metrics.period[0].submits_failed / metrics.period[0].submits * 100), trend_rate: metrics.period[0].submit_failed_trend_rate }">
          <div class="text-center conversionPathMetric" style="left: 556px; top: 184px;"
               :set="metric = { ...metric, badge_color: metric.trend_rate !== 0 ? (metric.trend_rate <= 0 ? 'badge-success' : 'badge-danger') : 'badge-secondary' }">
            <span class="material-icons text-primary mb-2">highlight_off</span>
            <div class="text-big font-weight-bold">{{ metric.name }}</div>
            <div class="text-big font-weight-bold text-primary m-1" style="font-size: 1.25rem !important;">{{ metric.metric | numeral }}</div>
            <div class="small-90" :class="metric.trend_rate !== 0 ? (metric.trend_rate <= 0 ? 'text-success' : 'text-danger') : 'text-secondary'" v-b-tooltip title="Trend metric">
              <metric-icon :upwards="metric.trend_rate !== 0 ? metric.trend_rate >= 0 : null" :color="metric.trend_rate <= 0 ? 'green' : 'red'" />
              {{ metric.trend_rate | numeral('0,0.0') }}%
            </div>
            <div class="font-weight-bold position-absolute text-right exatom-grey-plus1 metricRate" style="top: 31px; right: 0; transform: translateX(-202px);">
              {{ metric.rate | numeral('0,0.0') }}%
            </div>
          </div>
        </div>
        <div @click="selectedConversionPath = (selectedConversionPath !== 'resubmits_success' ? 'resubmits_success' : null)"
             :set="metric = { name: 'Re-submit success', metric: metrics.period[0].resubmits_success, rate: (metrics.period[0].resubmits_success / metrics.period[0].submits_failed * 100), trend_rate: metrics.period[0].resubmit_success_trend_rate }">
          <div class="text-center conversionPathMetric" style="left: 756px; top: 77px;"
               :set="metric = { ...metric, badge_color: metric.trend_rate !== 0 ? (metric.trend_rate >= 0 ? 'badge-success' : 'badge-danger') : 'badge-secondary' }">
            <span class="icon-flag-blue mt-2 mb-2">&nbsp;</span>
            <div class="text-big font-weight-bold">{{ metric.name }}</div>
            <div class="text-big font-weight-bold text-primary m-1" style="font-size: 1.25rem !important;">{{ metric.metric | numeral }}</div>
            <div class="small-90" :class="metric.trend_rate !== 0 ? (metric.trend_rate >= 0 ? 'text-success' : 'text-danger') : 'text-secondary'" v-b-tooltip title="Trend metric">
              <metric-icon :upwards="metric.trend_rate !== 0 ? metric.trend_rate >= 0 : null" :color="metric.trend_rate >= 0 ? 'green' : 'red'" />
              {{ metric.trend_rate | numeral('0,0.0') }}%
            </div>
            <div class="font-weight-bold position-absolute text-right exatom-grey-plus1 metricRate" style="top: 107px; right: 0; transform: translateX(-202px);">
              {{ metric.rate | numeral('0,0.0') }}%
            </div>
          </div>
        </div>
        <div @click="selectedConversionPath = (selectedConversionPath !== 'submits_abandon' ? 'submits_abandon' : null)"
             :set="metric = { name: 'Initial submits but abandoned', metric: metrics.period[0].submits_abandon, rate: (metrics.period[0].submits_abandon / metrics.period[0].submits_failed * 100), trend_rate: metrics.period[0].submit_abandon_trend_rate }">
          <div class="text-center conversionPathMetric" style="left: 756px; top: 282px;"
               :set="metric = { ...metric, badge_color: metric.trend_rate !== 0 ? (metric.trend_rate <= 0 ? 'badge-success' : 'badge-danger') : 'badge-secondary' }">
            <span class="material-icons text-danger mb-2">highlight_off</span>
            <div class="text-big font-weight-bold">{{ metric.name }}</div>
            <div class="text-big font-weight-bold text-primary m-1" style="font-size: 1.25rem !important;">{{ metric.metric | numeral }}</div>
            <div class="small-90" :class="metric.trend_rate !== 0 ? (metric.trend_rate <= 0 ? 'text-success' : 'text-danger') : 'text-secondary'" v-b-tooltip title="Trend metric">
              <metric-icon :upwards="metric.trend_rate !== 0 ? metric.trend_rate >= 0 : null" :color="metric.trend_rate <= 0 ? 'green' : 'red'" />
              {{ metric.trend_rate | numeral('0,0.0') }}%
            </div>
            <div class="font-weight-bold position-absolute text-right exatom-grey-plus1 metricRate" style="top: 31px; right: 0; transform: translateX(-202px);">
              {{ metric.rate | numeral('0,0.0') }}%
            </div>
          </div>
        </div>
        <div @click="selectedConversionPath = (selectedConversionPath !== 'conversions' ? 'conversions' : null)"
             :set="metric = { name: 'Conversions', metric: metrics.period[0].conversions, trend_rate: metrics.period[0].conversion_trend_rate }">
          <div class="text-center conversionPathMetric" style="left: 986px; top: -34px; width: 113px;"
               :set="metric = { ...metric, badge_color: metric.trend_rate !== 0 ? (metric.trend_rate >= 0 ? 'badge-success' : 'badge-danger') : 'badge-secondary' }">
            <span class="icon-flag-blue mt-2 mb-2">&nbsp;</span>
            <div class="text-big font-weight-bold">{{ metric.name }}</div>
            <div class="text-big font-weight-bold text-primary m-1" style="font-size: 1.25rem !important;">{{ metric.metric | numeral }}</div>
            <div class="small-90" :class="metric.trend_rate !== 0 ? (metric.trend_rate >= 0 ? 'text-success' : 'text-danger') : 'text-secondary'" v-b-tooltip title="Trend metric">
              <metric-icon :upwards="metric.trend_rate !== 0 ? metric.trend_rate >= 0 : null" :color="metric.trend_rate >= 0 ? 'green' : 'red'" />
              {{ metric.trend_rate | numeral('0,0.0') }}%
            </div>
            <div class="font-weight-bold position-absolute text-right exatom-grey-plus1 metricRate" style="top: 44px; right: 0; transform: translateX(-162px);">
              {{ (metrics.period[0].submits_once_success / metrics.period[0].conversions * 100) | numeral('0,0.0') }}%
            </div>
            <div class="font-weight-bold position-absolute text-right exatom-grey-plus1 metricRate" style="top: 106px; right: 0; transform: translateX(-162px);">
              {{ (metrics.period[0].resubmits_success / metrics.period[0].conversions * 100) | numeral('0,0.0') }}%
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Selected conversion-path details -->
    <div class="row card card-body mb-3 mx-0 pb-3 text-left" v-if="selectedConversionPath">
      <div class="font-weight-bold text-primary cursor-pointer" style="position: absolute; right: 24px; top: 16px;" @click="$event.target.parentElement.style.display = 'none'; selectedConversionPath = null;">&cross;</div>
      <h4 class="card-title line-after mb-4">{{ metricsForSelectedConversionPath.items[0].name }} insights</h4>
      <b-table v-if="metricsForSelectedConversionPath.items.length && Object.keys(metricsForSelectedConversionPath.items[0]).length > 1"
               :items="metricsForSelectedConversionPath.items"
               :fields="metricsForSelectedConversionPath.fields"
               striped fixed responsive>
        <template #cell(form_name)="{ item }">
          <div class="text-right font-weight-bold">
            <template v-if="form(item.form_hash)">
              <router-link :to="{ name: 'form-insights-for-form', params: { id: item.form_hash } }">{{ item.form_name }}</router-link>
            </template>
            <template v-else>
              <span class="badge badge-pill badge-secondary py-1 px-2" style="transform: scale(75%); transform-origin: right; vertical-align: top;">ARCHIVED</span>
              {{ item.form_name }}
            </template>
          </div>
        </template>
        <template #cell(total_abandoned_value)="{ item }">
          <span v-if="form(item.form_hash) && form(item.form_hash).conversion_value > 0">
            {{ $root.clientCurrencyFormat(item.total_abandoned_value) }}
            <span v-if="item.highestOpportunity === true" class="badge badge-pill badge-danger py-1 px-2 m-0 ml-2" style="vertical-align: top;">Highest opportunity</span>
          </span>
          <span v-else-if="form(item.form_hash)">
            n/a
            <span v-if="canEdit" class="material-icons md-14 arrow-east">east</span>
            <router-link :to="{ name: 'forms-edit', params: { id: item.form_hash }, query: { src: 'dashboard' } }" v-if="canEdit">
              Set conversion value
            </router-link>
          </span>
          <span v-else>
            n/a
            <span v-if="canEdit" class="material-icons md-14 arrow-east">east</span>
            this form is archived
          </span>
        </template>
        <template #cell(total_conversion_value)="{ item }">
          <span v-if="form(item.form_hash) && form(item.form_hash).conversion_value > 0">
            {{ $root.clientCurrencyFormat(item.total_conversion_value) }}
            <span v-if="item.highestOpportunity === true" class="badge badge-pill badge-success py-1 px-2 m-0 ml-2" style="vertical-align: top;">Best performer</span>
          </span>
          <span v-else-if="form(item.form_hash)">
            n/a
            <span v-if="canEdit" class="material-icons md-14 arrow-east">east</span>
            <router-link :to="{ name: 'forms-edit', params: { id: item.form_hash }, query: { src: 'dashboard' } }" v-if="canEdit">
              Set conversion value
            </router-link>
          </span>
          <span v-else>
            n/a
            <span v-if="canEdit" class="material-icons md-14 arrow-east">east</span>
            this form is archived
          </span>
        </template>
        <template #cell()="data">
          <div v-if="data.field.label.toLowerCase() === 'rate'">
            {{ data.value ? data.value.toFixed(2) : 0 }}%
            <span v-if="data.item.bestPerformer === true" class="badge badge-pill badge-success py-1 px-2 m-0 ml-2" style="vertical-align: top;">Best performer</span>
            <span v-else-if="data.item.worstPerformer === true" class="badge badge-pill badge-danger py-1 px-2 m-0 ml-2" style="vertical-align: top;">Worst performer</span>
          </div>
          <div v-else-if="data.field.label.toLowerCase() === 'trend'">
            <div :class="data.value ? (data.item.trendPositiveIsGood ? (data.value > 0 ? 'text-success' : 'text-danger') : (data.value < 0 ? 'text-success' : 'text-danger')) : 'text-secondary'">
              <metric-icon v-if="data.value" :upwards="data.value > 0"
                           :color="data.item.trendPositiveIsGood ? (data.value > 0 ? 'green' : 'red') : (data.value < 0 ? 'green' : 'red')" />{{ data.value | numeral('0,0.0') }}%
            </div>
          </div>
          <div v-else-if="data.field.label.toLowerCase().indexOf('time') !== -1 || data.field.label.toLowerCase().indexOf('duration') !== -1">
            {{ dateFormatter.duration(data.value, "seconds").format("m[m] s[s]") }}
          </div>
          <div v-else>
            {{ data.value }}
          </div>
        </template>
      </b-table>
      <span v-if="metricsForSelectedConversionPath.items.length === 1 && Object.keys(metricsForSelectedConversionPath.items[0]).length === 1">
        No detailed data available.
      </span>
    </div>

    <!-- Setup modal -->
    <b-modal id="modal-setup" hide-header hide-footer v-slot="{close}" @hide="stopSetup">
      <button type="button" aria-label="Close" class="close text-primary" style="margin-top: -8px;" @click="close">×</button>
      <div class="p-4">
        <progress-bar :current-step="step" :steps="['Setup', 'Install tags', 'Verify tags', 'Add forms']" />
      </div>
      <div v-if="step === 0" class="p-4">
        <div class="text-center text-primary">
          <span class="icon-rocket-blue mb-4" style="width: 60px; height: 60px;">&nbsp;</span>
        </div>
        <h3 class="text-center mb-4">Welcome {{ $root.me.name }}!</h3>
        <p style="text-align: center;">
          Congratulations on starting your journey with us. We're excited to get you ready to grow your business.
          Just a few more steps to get you started.
        </p>
        <div class="text-primary small mb-3" style="text-align: center;">
          You can return to the dashboard at any time to continue your account setup.
        </div>
        <div class="text-center">
          <button type="button" class="btn btn-create btn-rounded" @click="step = 1">START SETUP</button>
        </div>
      </div>
      <div v-else-if="step === 1" class="p-4">
        <div class="text-center text-primary">
          <i class="material-icons" style="font-size: 60px;">business</i>
        </div>
        <h3 class="text-center">Setup</h3>
        <p v-if="showCompanyInSetup">Confirm your company name and enter the website you want to start analyzing your forms for.</p>
        <p v-else>Enter the website you want to start analyzing your forms for.</p>
        <form id="dashboard-setup-company-and-website" @submit.prevent>
          <div class="form-group" v-if="showCompanyInSetup">
            <span class="text-primary font-weight-bold">Company name</span>
            <b-input id="setup-company" type="text" class="form-control" placeholder="Company name" autocomplete="off" data-1p-ignore v-model="setup.company" required></b-input>
          </div>
          <div class="form-group">
            <span class="text-primary font-weight-bold">Your website</span>
            <b-input id="setup-website" type="text" autocomplete="off" data-1p-ignore class="form-control" placeholder="example.com" v-model="setup.website" required autofocus
                     :state="isValidDomain"
                     @input="isValidDomain = null"
                     @change="domainValidator($event)"></b-input>
            <span class="small text-danger" v-if="isValidDomain === false">Please enter a valid domain or url.</span>
          </div>
          <div class="text-center">
            <button type="button" class="btn btn-create btn-rounded px-4" data-style="zoom-in"
                       :disabled="!setup.company || !setup.website"
                       :loading="validatingDomain"
                       @click="submitCompanyDetails()">CONTINUE</button>
          </div>
        </form>
      </div>
      <div v-else-if="step === 2" class="p-4">
        <div class="text-center text-primary">
          <i class="material-icons" style="font-size: 60px;">code</i>
        </div>
        <h3 class="text-center">Activating Exatom on your website</h3>
        <div v-if="!manualTagInstall">
          <div class="text-center mb-3">Our <b>Google Tag Manager</b> integration is the fastest way to get started.</div>
          <div class="row mb-3">
            <div class="col-md-6">
              <div class="card card-body">
                <h4 class="text-primary">Client code</h4>
                <div class="monospace font-weight-bolder d-flex flex-column justify-content-center align-items-center" style="font-size: 2rem;">
                  {{ $root.client.client_code }}
                  <button type="button" class="btn btn-transparent p-0 text-primary text-uppercase font-weight-bold mt-2" style="font-size: 0.75rem; font-family: Roboto;"
                          v-clipboard:copy="$root.client.client_code">
                        <span @click="e => { e.target.closest('BUTTON').firstElementChild.classList.add('text-success-darker'); e.target.closest('BUTTON').firstElementChild.lastElementChild.innerText = 'COPIED' }">
                          <i class="material-icons" style="font-size: 0.85rem; vertical-align: text-top;">content_copy</i>
                          <span>COPY</span>
                        </span>
                  </button>
                </div>
              </div>
            </div>
            <div class="col-md-6">
              <div class="card card-body">
                <h4 class="text-primary">Conversion code</h4>
                <div class="monospace font-weight-bolder d-flex flex-column justify-content-center align-items-center" style="font-size: 2rem;">
                  {{ conversionCode }}
                  <button type="button" class="btn btn-transparent p-0 text-primary text-uppercase font-weight-bold mt-2" style="font-size: 0.75rem; font-family: Roboto;"
                          v-clipboard:copy="conversionCode">
                        <span @click="e => { e.target.closest('BUTTON').firstElementChild.classList.add('text-success-darker'); e.target.closest('BUTTON').firstElementChild.lastElementChild.innerText = 'COPIED' }">
                          <i class="material-icons" style="font-size: 0.85rem; vertical-align: text-top;">content_copy</i>
                          <span>COPY</span>
                        </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div class="row mb-2">
            <div class="col">
              <div class="card card-body text-left">
                <img src="@/assets/integrations/gtm.svg" class="logo">

                <ol class="pl-4 mt-2 mb-0">
                  <li class="d-none d-md-list-item">You'll need your Client and Conversion codes displayed on your left.</li>
                  <li class="d-md-none">You'll need your Client and Conversion codes displayed above.</li>
                  <li>Follow our <a href="https://support.exatom.io/portal/en/kb/articles/google-tag-manager" target="_blank">installation guide</a>.</li>
                  <li>Visit your website and test your form and submit it.</li>
                  <li>Continue the set up over here by continuing to the next step.</li>
                </ol>
              </div>
            </div>
          </div>
          <a @click="manualTagInstall = !manualTagInstall" class="small text-primary">Do you need to manually install tags or send them to my development team?</a>
        </div>
        <div v-if="manualTagInstall">
          <div class="card card-body text-left">
            <p>Add the tags to the end of the <code class="inline text-danger">&lt;body&gt;</code> section of your website.</p>
            <p class="m-0 mt-4">
              <span class="badge badge-pill badge-primary py-1 px-2 mr-1">1</span><span class="text-primary font-weight-bold">Exatom tag</span><br/>
              Install this tag on <b>all pages</b> to start measuring form activity.
            </p>
            <div class="mb-4">
              <button type="button" class="btn btn-transparent p-0 text-primary text-uppercase font-weight-bold" style="font-size: 0.75rem;"
                      v-clipboard:copy="setup.tags.filter(tag => tag.type === 'GENERIC')[0].contents">
                <span @click="e => e.target.classList.add('text-success-darker')"><i class="material-icons" style="font-size: 0.85rem; vertical-align: text-top;">content_copy</i> COPY TAG</span>
              </button>
            </div>
            <p class="m-0">
              <span class="badge badge-pill badge-primary py-1 px-2 mr-1">2</span><span class="text-primary font-weight-bold">Conversion tag</span><br/>
              Install this tag <b><span class="text-danger">only</span> on the thank you page(s)</b> that visitors see after completing a form.
            </p>
            <div class="mb-4">
              <button type="button" class="btn btn-transparent p-0 text-primary text-uppercase font-weight-bold" style="font-size: 0.75rem;"
                      v-clipboard:copy="setup.tags.filter(tag => tag.type === 'CONVERSION')[0].contents">
                <span @click="e => e.target.classList.add('text-success-darker')"><i class="material-icons" style="font-size: 0.85rem; vertical-align: text-top;">content_copy</i> COPY TAG</span>
              </button>
            </div>
            <p class="m-0">
              <span class="badge badge-pill badge-secondary py-1 px-2 mr-1">3</span><span class="text-secondary font-weight-bold">Audience activation tag <sup>(optional)</sup></span><br/>
              To use our audience feature, install this tag on <b>all pages</b>. Within the EU and many other jurisdictions this tag needs to be placed <b><span class="text-danger">behind</span></b> a consent management system.
            </p>
            <div class="mb-4">
              <button type="button" class="btn btn-transparent p-0 text-secondary text-uppercase font-weight-bold" style="font-size: 0.75rem;"
                      v-clipboard:copy="setup.tags.filter(tag => tag.type === 'AUDIENCE')[0].contents">
                <span @click="e => e.target.classList.add('text-success-darker')"><i class="material-icons" style="font-size: 0.85rem; vertical-align: text-top;">content_copy</i> COPY TAG</span>
              </button>
            </div>
            <div class="text-center">
              <a href="#" @click.prevent="doDownloadTagDocument"><i class="material-icons small">download</i> Download documentation</a>
            </div>
          </div>
          <div class="mt-2">
            <a @click="manualTagInstall = !manualTagInstall" class="small text-primary">Use Google Tag Manager to install tags</a>
          </div>
        </div>

        <div class="text-center">
          <button type="button" class="btn btn-create btn-rounded mt-4 mb-3" @click="step = 3">VERIFY TAG INSTALLATION</button>
        </div>
      </div>
      <div v-else-if="step === 3" class="p-4">
        <div class="text-center text-primary">
          <i class="material-icons" style="font-size: 60px;" @click="bypassTagVerification">playlist_add_check</i>
        </div>
        <h3 class="text-center">Verify tags</h3>
        <div v-if="verifyTags.status !== 'cancelled'">
          <p>Now that the tags are in place we want to ensure we’re receiving data as expected.</p>
          <div class="row mb-2">
            <div class="col-md-6" :key="tag.key"
                 v-for="tag in [{key: 'GENERIC', label: 'Exatom tag'}, {key: 'CONVERSION', label: 'Conversion tag'}]">
              <div class="font-weight-bold">{{ tag.label }}</div>
              <div v-if="tagVerificationBypassed || (((verifyTags.output && verifyTags.output.metrics) || {})[tag.key] || {}).hits">
                <span class="badge badge-pill badge-primary py-2 px-2 text-uppercase small">Verified</span>
              </div>
              <div v-else><i class="spinner-grow" style="width: 16px; height: 16px; color: #50f0d5;"></i></div>
            </div>
          </div>
        </div>
        <div class="d-flex small text-danger mt-4" v-if="verifyTags.status === 'slow'">
          <i class="material-icons mr-2">error_outline</i>
          <div>Some tags show no activity, please visit your website and/or perform a conversion to successfully complete
            the verification process.</div>
        </div>
        <div v-if="verifyTags.status === 'cancelled'" class="mt-4">
          <div class="d-flex small text-danger">
            <i class="material-icons">error_outline</i>
            Some tags show no activity, please make sure your website is tagged correctly.
          </div>
          <div class="text-center">
            <button type="button" class="btn btn-create btn-rounded px-4" @click="step = 3.1">TAGS INSTALLED, VERIFY NOW</button>
          </div>
        </div>
        <div class="text-center mt-4" v-else>
          <div class="mb-4" v-if="verifyTags.status === true">Looking good, continue to start analyzing your first form now.</div>
          <button type="button" class="btn btn-transparent text-primary" @click="step = 2" v-if="verifyTags.status !== true">
            <i class="material-icons" style="font-size: 0.7rem;">arrow_back_ios</i> BACK TO TAGS
          </button>
          <router-link tag="button" class="btn btn-create btn-rounded px-4" :disabled="verifyTags.status !== true" :to="{ name: 'forms-add', query: { src: 'dashboardSetup' } }">Add your first form</router-link>
        </div>
      </div>
      <div v-else-if="step === 4" class="p-4">
        <div class="text-center text-primary">
          <i class="material-icons" style="font-size: 60px;">playlist_add</i>
        </div>
        <h3 class="text-center">Add forms</h3>
        <p>Last step is to add a form that will allow you to start analysing your customer behavior.</p>
        <div class="text-center">
          <router-link tag="button" class="btn btn-create btn-rounded px-4" :to="{ name: 'forms-add', query: { src: 'dashboardSetup' } }">Add your first form</router-link>
        </div>
      </div>
      <div v-else-if="step === 1010" class="p-4">
        <div class="text-center text-primary">
          <span class="material-symbols-outlined p-1" style="font-size: 60px;">storefront</span>
        </div>
        <h3 class="text-center">Shopify store set up completed</h3>
        <p>We've enabled tagging and setup your checkout. Once your customers start engaging with your checkout analytics will be available.</p>
        <div class="text-center">
          <router-link tag="button" class="btn btn-create btn-rounded px-4" :to="{ name: 'field-metrics' }">Go to Field Metrics</router-link>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { downloadTagDocumentMixin } from '@/utils'
import ProgressBar from '@/components/setup/ProgressBar'
import MetricIcon from './MetricIcon'
import { DateTime } from 'luxon'
import { getOnboardingStatus, onboardingRouteForStatus } from '@/utils/onboardingRouting'

export default {
  name: 'dashboard',
  components: { MetricIcon, ProgressBar },
  mixins: [downloadTagDocumentMixin],
  metaInfo: {
    title: 'Dashboard'
  },
  data () {
    return {
      includeToday: false,
      metrics: null,
      metricColors: {
        red: getComputedStyle(document.body).getPropertyValue('--danger'),
        green: getComputedStyle(document.body).getPropertyValue('--success')
      },
      setup: {
        company: null,
        tags: null,
        website: null
      },
      step: 0,
      terms: '',
      verifyTags: { interval: null, output: {}, status: null },
      validatingDomain: false,
      isValidDomain: null,
      tagVerificationBypassed: false,
      performanceAlerts: {},
      motivationalWidgets: {},
      showTrends: true,
      availableDates: [
        { text: '7 days', value: 7 },
        { text: '14 days', value: 14 },
        { text: '30 days', value: 30 }
      ],
      loading: null,
      selectedDate: null, // defaults to 14d, see loadMetrics()
      selectedDateFrom: null,
      selectedDateTo: null,
      selectedConversionPath: null,
      forms: null,
      dateFormatter: null,
      manualTagInstall: false,
      onboardingRedirecting: false,
      setupStatusResolved: false
    }
  },
  computed: {
    conversionCode () {
      if (!this.setup?.tags?.length) return
      return this.setup?.tags.find(t => t.type === 'CONVERSION').tag_code
    },
    showCompanyInSetup () {
      return this.$root.me.grants.combined.length === 1
    },
    canEdit () {
      return this.$root.hasRole('admin.internal') ||
        this.$root.hasRole('admin.company') ||
        this.$root.hasRole('configuration.modify')
    },
    showSetupPrompt () {
      return this.setupStatusResolved && this.step < 4
    },
    metricsForSelectedConversionPath () {
      if (!this.selectedConversionPath || !this.metrics || !this.metrics.conversionPath) return

      let keys = []
      let name = ''
      let trendPositiveIsGood = true
      switch (this.selectedConversionPath) {
        case 'sessions':
          name = 'Sessions'
          keys = ['sessions', 'sessions_trend_rate']
          break
        case 'starts':
          name = 'Starts'
          keys = ['starts', 'start_rate', 'start_trend_rate']
          break
        case 'submits':
          name = 'Submits'
          keys = ['submits', 'submit_rate', 'submit_trend_rate']
          break
        case 'abandoners':
          name = 'Abandoners after start'
          keys = ['abandoners', 'abandonment_rate', 'abandonment_trend_rate', 'total_abandoned_value']
          trendPositiveIsGood = false
          break
        case 'initial_abandoners':
          name = 'Initial abandoners'
          keys = ['initial_abandoners', 'initial_abandonment_rate', 'initial_abandonment_trend_rate']
          trendPositiveIsGood = false
          break
        case 'pre_start_abandoners':
          name = 'Abandoners before start'
          keys = ['pre_start_abandoners', 'pre_start_abandonment_rate', 'pre_start_abandonment_trend_rate']
          trendPositiveIsGood = false
          break
        case 'submits_once_success':
          name = 'Submit and convert'
          keys = ['submits_once_success', 'submit_once_success_rate', 'submit_once_success_trend_rate']
          break
        case 'submits_failed':
          name = 'Submit failed'
          keys = ['submits_failed', 'submit_failed_rate', 'submit_failed_trend_rate']
          trendPositiveIsGood = false
          break
        case 'resubmits_success':
          name = 'Re-submit success'
          keys = ['resubmits_success', 'resubmit_success_rate', 'resubmit_success_trend_rate']
          break
        case 'submits_abandon':
          name = 'Initial submits but abandoned'
          keys = ['submits_abandon', 'submit_abandon_rate', 'submit_abandon_trend_rate']
          trendPositiveIsGood = false
          break
        case 'conversions':
          name = 'Conversions'
          keys = ['conversions', 'conversion_rate', 'conversion_trend_rate', 'total_conversion_value']
          break
        case 'hesitation_time':
          name = 'Hesitation time'
          keys = ['hesitation_time', 'hesitation_time_trend_rate']
          trendPositiveIsGood = false
          break
        case 'duration_to_conversion':
          name = 'Time to conversion'
          keys = ['duration_to_conversion', 'duration_to_conversion_trend_rate']
          trendPositiveIsGood = false
          break
        case 'errors':
          name = 'Errors'
          keys = ['errors', 'error_rate', 'error_trend_rate']
          trendPositiveIsGood = false
          break
      }

      const items = this.metrics.conversionPath.map(metric => {
        // Hide ones with no data
        if (!metric.form_name.length) return

        const result = {
          name: name,
          form_hash: metric.form_hash,
          form_name: metric.form_name,
          trendPositiveIsGood: trendPositiveIsGood
        }

        for (const key of keys) {
          result[key] = metric[key]
        }

        return result
      }).filter(Boolean)

      let rateColumn = null
      let trendColumn = null
      let valueColumn = null
      for (const key of keys) {
        if (key.indexOf('_trend_') === -1 && key.indexOf('_rate') !== -1) {
          rateColumn = key
        } else if (key.indexOf('_trend_') !== -1) {
          trendColumn = key
        } else if (key.indexOf('_value') !== -1) {
          valueColumn = key
        }
      }

      // If there is just one record, no need to show best performer :)
      if (rateColumn && trendColumn && items.length > 1) {
        /*
         * Get best and worst performer
         */
        let worstPerformer = items.filter(i => i[rateColumn] !== 0).minForObjAttr(rateColumn)
        let bestPerformer = items.filter(i => i[rateColumn] !== 0).maxForObjAttr(rateColumn)

        // Positive trend is good or not?
        if (!trendPositiveIsGood) {
          // Swap
          [worstPerformer, bestPerformer] = [bestPerformer, worstPerformer]
        }

        if (worstPerformer && items.filter(i => i[rateColumn] === worstPerformer[rateColumn]).length > 1) {
          // Multiple items w/ the same worst rate, look at the trend column
          worstPerformer = items.filter(i => i[trendColumn] !== 0).maxForObjAttr(trendColumn)
        }
        if (bestPerformer && items.filter(i => i[rateColumn] === bestPerformer[rateColumn]).length > 1) {
          // Multiple items w/ the same best rate, look at the trend column
          bestPerformer = items.filter(i => i[trendColumn] !== 0).maxForObjAttr(trendColumn)
        }

        // Add flags
        if (worstPerformer) worstPerformer.worstPerformer = true
        if (bestPerformer) bestPerformer.bestPerformer = true

        /*
         * Highest optimization opportunity value
         */
        const highestOpportunity = items.filter(i => i[valueColumn] !== 0).maxForObjAttr(valueColumn)

        // Add flags
        if (highestOpportunity) highestOpportunity.highestOpportunity = true
      }

      const fields = [{ key: 'form_name', sortable: true }, { key: keys[0], label: name }]
      keys.forEach(k => fields.push({
        key: k,
        label: (k.indexOf('_trend_') !== -1 ? 'Trend' : (k.indexOf('_rate') !== -1 ? 'Rate' : (name.slice(0, 5).toLowerCase() === k.slice(0, 5).toLowerCase() ? name : k.replaceAll('_', ' ')))),
        sortable: true
      }))

      return {
        items: items.length ? items : [{ name: name }],
        fields: fields
      }
    }
  },
  methods: {
    async doDownloadTagDocument () {
      await this.downloadTagDocument(this.setup.tags)
    },
    async loadMetrics (period, daily = true, dimensions = []) {
      if (period === null) {
        period = 14
      }
      this.selectedDateFrom = DateTime.now().minus({ days: period }).toFormat('yyyy-LL-dd')
      this.selectedDateTo = DateTime.now().minus({ days: this.includeToday ? 0 : 1 }).toFormat('yyyy-LL-dd')
      const data = {
        from: this.selectedDateFrom,
        to: this.selectedDateTo,
        time_group: daily ? 'DAY' : 'PERIOD',
        dimensions: dimensions
      }

      return (await this.$api.post('/metrics/forms', data, this.$root.client.client_hash)).data.metrics
    },
    bypassTagVerification () {
      this.tagVerificationBypassed = true
      this.verifyTags.output = {
        metrics: {
          GENERIC: { hits: 1 },
          CONVERSION: { hits: 1 }
        }
      }
    },
    // Also used on WebsiteForm (code duplicate)
    async domainValidator (domain) {
      if (!domain) return

      this.validatingDomain = true
      const result = (await this.$api.post(
        '/domains/validate',
        { domains: [domain] },
        this.$root.client.client_hash
      )).data
      this.validatingDomain = false

      this.isValidDomain = result.length > 0
      if (this.isValidDomain) {
        this.setup.website = result[0]
      }
    },
    async submitCompanyDetails () {
      await this.domainValidator(this.setup.website)
      if (this.isValidDomain !== true) {
        return
      }

      try {
        await this.$api.post(
          '/onboarding/website',
          {
            company_name: this.setup.company,
            website_url: this.setup.website
          },
          this.$root.client.client_hash
        )

        // Reload client selector
        this.$root.reload()

        if (!this.setup.tags) {
          this.setup.tags = (await this.$api.get('/tags', this.$root.client.client_hash)).data
        }
        this.step = 2
      } catch (e) {}
    },
    async redirectToRealOnboardingIfNeeded (onboardingStatus = null) {
      if (
        this.onboardingRedirecting ||
        !this.canEdit ||
        !this.$root.client ||
        this.$route.name !== 'dashboard' ||
        this.$root.setupStatusStep === 1000
      ) {
        return false
      }

      this.onboardingRedirecting = true
      try {
        if (!onboardingStatus) {
          onboardingStatus = await getOnboardingStatus(this.$api, this.$root.client.client_hash)
        }

        const onboardingRoute = onboardingRouteForStatus(onboardingStatus, this.$route.name)

        if (onboardingRoute) {
          await this.$router.push({
            ...onboardingRoute,
            query: this.$route.query.client_hash ? { client_hash: this.$route.query.client_hash } : {}
          })
          return true
        }
      } catch (e) {
        console.warn('Unable to evaluate onboarding redirect; keeping dashboard setup flow disabled.', e)
      } finally {
        this.onboardingRedirecting = false
      }

      return false
    },
    onboardingSetupStep (onboardingStatus) {
      return this.$root.setupStepFromOnboardingStatus
        ? this.$root.setupStepFromOnboardingStatus(onboardingStatus)
        : 0
    },
    stopSetup () {
      clearInterval(this.verifyTags.interval)
    },
    form (hash) {
      if (!hash || typeof hash === 'undefined' || !this.forms) return
      return this.forms.filter(f => f.id === hash)[0]
    },
    async changeSelectedDate () {
      if (!this.$root.client || this.loading) return

      if (this.selectedDate) {
        sessionStorage.insights_range = localStorage.insights_range = this.availableDates.filter((d) => d.value === this.selectedDate)[0].text
      }
      const [
        period,
        conversionPath,
        forms
      ] = await Promise.all([
        this.loadMetrics(this.selectedDate, false),
        this.loadMetrics(this.selectedDate, false, ['form_name']),
        this.$api.get('/forms?with=null', this.$root.client.client_hash)
      ])

      this.metrics = {
        period: period,
        conversionPath: conversionPath // Data for hover/clicks
      }
      this.forms = forms.data
    }
  },
  watch: {
    step: {
      handler () {
        // Restarting step 3
        if (this.step === 3.1) {
          this.step = 3
          return
        }

        clearInterval(this.verifyTags.interval)

        if (this.step === 3) {
          this.verifyTags.output = null
          this.verifyTags.status = null

          const slowTimer = setTimeout(() => { this.verifyTags.status = 'slow' }, 5000)
          const cancelTimer = setTimeout(() => {
            this.verifyTags.status = 'cancelled'
            clearTimeout(slowTimer)
            clearTimeout(cancelTimer)
            clearInterval(this.verifyTags.interval)
          }, 300 * 1000) // 5 minutes

          this.verifyTags.interval = setInterval(async () => {
            try {
              // Cancel if not on dashboard anymore
              if (this.$route.path !== '/') {
                clearTimeout(slowTimer)
                clearTimeout(cancelTimer)
                clearInterval(this.verifyTags.interval)
              }

              // Verify tags
              this.verifyTags.output = (await this.$api.post('/onboarding/tags/verify', {}, this.$root.client.client_hash)).data

              if (this.tagVerificationBypassed || (this.verifyTags.output.metrics.GENERIC.hits && this.verifyTags.output.metrics.CONVERSION.hits)) {
                clearTimeout(slowTimer)
                clearTimeout(cancelTimer)
                clearInterval(this.verifyTags.ixnterval)
                this.verifyTags.status = true
              }
            } catch (error) {
              clearTimeout(slowTimer)
              clearTimeout(cancelTimer)
              clearInterval(this.verifyTags.interval)
            }
          }, 2500)
        }
      },
      immediate: true
    },
    '$root.client': {
      async handler () {
        if (this.$root.client && !this.loading) {
          this.metrics = null
          this.setupStatusResolved = false
          this.loading = true

          const onboardingStatus = await getOnboardingStatus(this.$api, this.$root.client.client_hash)

          if (await this.redirectToRealOnboardingIfNeeded(onboardingStatus)) {
            return
          }

          this.step = this.onboardingSetupStep(onboardingStatus)
          this.setupStatusResolved = true

          if (this.step < 4 && this.canEdit) {
            this.loading = false
            return
          }

          if (this.$root.hasRole('admin.*') || this.$root.hasRole('configuration.*') || this.$root.hasRole('reporting')) {
            [sessionStorage, localStorage].forEach((storage) => {
              if (storage.insights_range) {
                this.selectedDate = this.availableDates.filter((d) => d.text === storage.insights_range)[0].value
              }
            })
            const [
              period
            ] = await Promise.all([
              this.loadMetrics(this.selectedDate, false)
            ])

            this.metrics = {
              period: period,
              conversionPath: [] // Data for hover/clicks >> async load below
            }

            // Highlight that you can hover/click on the conversionPath
            // const maxShows = 5
            // const repeatAfterDays = 14
            // const repeats = 1
            // if (typeof localStorage.conversionPathMetricSampleHover === 'undefined') {
            //   localStorage.conversionPathMetricSampleHover = 0
            // }
            // if (
            //   typeof localStorage.conversionPathMetricSampleHoverDatetime !== 'undefined' &&
            //   (Date.now() - localStorage.conversionPathMetricSampleHoverDatetime) > repeatAfterDays * 86400 * 1000 &&
            //   localStorage.conversionPathMetricSampleHover >= maxShows
            // ) {
            //   // 2 weeks ago we last showed the sample to hover/click on, show again as a reminder
            //   localStorage.conversionPathMetricSampleHover -= repeats
            // }
            // if (localStorage.conversionPathMetricSampleHover < maxShows) {
            //   localStorage.conversionPathMetricSampleHover++
            setTimeout(() => this.$refs.conversionPathStarts?.classList.add('conversionPathMetricSampleHover'), 500)
            setTimeout(() => this.$refs.conversionPathStarts?.classList.remove('conversionPathMetricSampleHover'), 1500)
            setTimeout(() => this.$refs.missedConversionOpportunity?.classList.add('missedConversionOpportunitySampleHover'), 500)
            setTimeout(() => this.$refs.missedConversionOpportunity?.classList.remove('missedConversionOpportunitySampleHover'), 1500)
            //   localStorage.conversionPathMetricSampleHoverDatetime = Date.now()
            // }
          }

          this.selectedConversionPath = null
          this.loading = false

          // Async load
          this.loadMetrics(this.selectedDate, false, ['form_name']).then(json => {
            this.metrics.conversionPath = json
          })
          this.$api.get('/forms?with=null', this.$root.client.client_hash).then(json => {
            this.forms = json.data
            this.includeToday = this.forms.length > 0 && !this.forms.filter(form => new Date().toDateString() !== new Date(form.created_at).toDateString()).length
            if (this.includeToday) {
              this.changeSelectedDate()
            }
          })
        }
      },
      immediate: true
    },
    selectedDate: {
       handler () {
        this.changeSelectedDate()
      },
      immediate: true
    }
  },

  mounted() {
    // Configure AI chat metadata to disable suggestions on dashboard
    this.$root.setAiChatMetadata({
      suggestionsEnabled: true,
      maxSuggestions: 4,
      pageType: 'dashboard',
      suggestionButtons: [
        {
          "label": "Explain Funnel",
          "prompt": "Explain how the form conversion funnel works and what each stage (Sessions, Starts, Submits, Conversions) represents."
        },
        {
          "label": "Explain Trends",
          "prompt": "Describe the meaning of the upward and downward trends in metrics such as hesitation time, conversion rate, and error rate."
        },
        {
          "label": "Identify Drop-Offs",
          "prompt": "Identify which steps in the funnel have the highest abandonment or failure rates and how they impact overall conversions."
        },
        {
          "label": "Recommend Improvements",
          "prompt": "Based on the current dashboard data, recommend specific actions to improve conversion rate and reduce missed opportunities."
        }
      ]
    });
  },
}
</script>
<style scoped>
/* Dashboard metrics */
.lines-top {
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
  height: 3rem;
  display: flex;
  align-items: center;
  justify-content: center;
}
.card {
  position: relative;
  text-align: center;
}
.card-metrics-center {
  position: relative;
  top: 50%;
  transform: translate(0%, -50%);
  z-index: 2;
}

/* On boarding */
.is-invalid {
  border-color: #ea8383 !important;
}
.is-valid {
  border-color: #48d8c0 !important;
}

.exatom-grey-plus1:hover {
  color: unset;
}

.missedConversionOpportunitySample {
  cursor: pointer;
  display: block;
  position: absolute;
  left: 50%;
  margin-left: -125px;
  top: 50%;
  margin-top: -125px;
  width: 250px;
  height: 250px;
  border: 125px solid var(--exatom-green);
  border-radius: 250px;
  opacity: 0.25;
  transition: transform 500ms;
  transform: scale(0);
  z-index: 2;
}
.missedConversionOpportunitySampleHover {
  opacity: 0.25;
  transition: transform 500ms;
  transform: scale(1);
}

.conversionPathMetricSample {
  cursor: pointer;
  display: block;
  position: absolute;
  left: -79px;
  top: 138px;
  width: 250px;
  height: 250px;
  border: 125px solid var(--exatom-green);
  border-radius: 250px;
  opacity: 0.25;
  transition: transform 500ms;
  transform: scale(0);
}
.conversionPathMetricSampleHover {
  opacity: 0.25;
  transition: transform 500ms;
  transform: scale(1);
}

.conversionPathMetric {
  position: absolute;
  width: 175px;
  font-size: 12px;
  //background: linear-gradient(#ff0000, #ff0000) no-repeat center/1px 100%;
}

.conversionPathMetric div.text-primary {
  //background: linear-gradient(#ff0000, #ff0000) no-repeat center/100px 1px;
}

.conversionPathMetric:before {
  content: "";
  cursor: pointer;
  display: block;
  position: absolute;
  left: 50%;
  margin-left: -125px;
  top: 50%;
  margin-top: -125px;
  width: 250px;
  height: 250px;
  border: 125px solid var(--exatom-green);
  border-radius: 250px;
  opacity: 0.25;
  transition: transform 500ms;
  transform: scale(0);
}
.conversionPathMetric:hover:before {
  opacity: 0.25;
  transition: transform 500ms;
  transform: scale(1);
}

.metricRate {
  width: 40px;
  text-align: right;
}
</style>
<style>
/* Conversion path date selector */
#dashboard-date .selector .btn {
  border: 0 solid transparent;
  padding: 4px 8px 4px 8px;
  font-size: 0.75rem;
}
#dashboard-date .selector label.active {
  background-color: var(--primary) !important;
  color: #FFF !important;
}
</style>
